import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import ReactApexChart from 'react-apexcharts';
import { API_BASE_URL } from "./config";

const RelatoriosLayout = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/relatorios/dashboard`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            setDashboardData(data);
        } catch (error) {
            console.error("Erro ao buscar dados do dashboard:", error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="p-24">Carregando relatórios...</div>;
    }

    if (!dashboardData) {
        return <div className="p-24">Erro ao carregar dados.</div>;
    }

    // Configurações dos Gráficos
    const receitaSeries = [{
        name: 'Receita',
        data: dashboardData.receitaPorMes?.map(item => item.total) || []
    }];
    const receitaOptions = {
        chart: { type: 'area', height: 350, toolbar: { show: false }, fontFamily: 'Nunito, sans-serif' },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.9, stops: [0, 90, 100] } },
        xaxis: { categories: dashboardData.receitaPorMes?.map(item => item.mes) || [], labels: { style: { colors: '#6B7280' } } },
        yaxis: { labels: { formatter: (val) => `R$ ${val}`, style: { colors: '#6B7280' } } },
        tooltip: { theme: 'light', y: { formatter: (val) => `R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` } },
        colors: ['#487FFF'],
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 }
    };

    const matriculasSeries = [{
        name: 'Matrículas',
        data: dashboardData.matriculasPorMes?.map(item => item.total) || []
    }];
    const matriculasOptions = {
        chart: { type: 'bar', height: 350, toolbar: { show: false }, fontFamily: 'Nunito, sans-serif' },
        plotOptions: { bar: { borderRadius: 4, horizontal: false, columnWidth: '55%' } },
        dataLabels: { enabled: false },
        xaxis: { categories: dashboardData.matriculasPorMes?.map(item => item.mes) || [], labels: { style: { colors: '#6B7280' } } },
        yaxis: { labels: { style: { colors: '#6B7280' } } },
        tooltip: { theme: 'light' },
        colors: ['#FF9F43'],
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 }
    };

    return (
        <div className="row gy-4">
            {/* Cards de Resumo */}
            <div className="col-xxl-3 col-sm-6">
                <div className="card p-24 radius-12 h-100">
                    <div className="d-flex align-items-center gap-3">
                        <div className="w-50-px h-50-px radius-12 bg-primary-50 d-flex justify-content-center align-items-center">
                            <Icon icon="solar:users-group-two-rounded-bold" className="text-primary-600 text-2xl" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-sm fw-medium">Alunos Ativos</span>
                            <h4 className="text-primary-light mb-0">{dashboardData.totalAlunosAtivos}</h4>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-xxl-3 col-sm-6">
                <div className="card p-24 radius-12 h-100">
                    <div className="d-flex align-items-center gap-3">
                        <div className="w-50-px h-50-px radius-12 bg-success-50 d-flex justify-content-center align-items-center">
                            <Icon icon="solar:wallet-bold" className="text-success-600 text-2xl" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-sm fw-medium">Receita Total</span>
                            <h4 className="text-primary-light mb-0">R$ {dashboardData.receitaTotal?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h4>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-xxl-3 col-sm-6">
                <div className="card p-24 radius-12 h-100">
                    <div className="d-flex align-items-center gap-3">
                        <div className="w-50-px h-50-px radius-12 bg-danger-50 d-flex justify-content-center align-items-center">
                            <Icon icon="solar:danger-circle-bold" className="text-danger-600 text-2xl" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-sm fw-medium">Inadimplência (Parc.)</span>
                            <h4 className="text-primary-light mb-0">{dashboardData.parcelasAtrasadas}</h4>
                        </div>
                    </div>
                </div>
            </div>

             <div className="col-xxl-3 col-sm-6">
                <div className="card p-24 radius-12 h-100">
                    <div className="d-flex align-items-center gap-3">
                        <div className="w-50-px h-50-px radius-12 bg-info-50 d-flex justify-content-center align-items-center">
                            <Icon icon="solar:course-up-bold" className="text-info-600 text-2xl" />
                        </div>
                        <div>
                            <span className="text-secondary-light text-sm fw-medium">Total de Matrículas</span>
                            <h4 className="text-primary-light mb-0">{dashboardData.totalMatriculas}</h4>
                        </div>
                    </div>
                </div>
            </div>

            {/* Gráficos */}
            <div className="col-lg-6">
                <div className="card h-100">
                    <div className="card-header border-bottom bg-base py-16 px-24">
                        <h6 className="text-lg fw-semibold mb-0">Evolução da Receita (Últimos 6 Meses)</h6>
                    </div>
                    <div className="card-body p-24">
                        <ReactApexChart options={receitaOptions} series={receitaSeries} type="area" height={350} />
                    </div>
                </div>
            </div>

            <div className="col-lg-6">
                <div className="card h-100">
                    <div className="card-header border-bottom bg-base py-16 px-24">
                        <h6 className="text-lg fw-semibold mb-0">Novas Matrículas (Últimos 6 Meses)</h6>
                    </div>
                    <div className="card-body p-24">
                        <ReactApexChart options={matriculasOptions} series={matriculasSeries} type="bar" height={350} />
                    </div>
                </div>
            </div>

            {/* Tabela de Matrículas por Curso */}
            <div className="col-12">
                <div className="card h-100">
                    <div className="card-header border-bottom bg-base py-16 px-24">
                        <h6 className="text-lg fw-semibold mb-0">Alunos por Curso</h6>
                    </div>
                    <div className="card-body p-24">
                        <div className="table-responsive">
                            <table className="table bordered-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Curso</th>
                                        <th>Quantidade de Alunos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dashboardData.alunosPorCurso.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.nomeCurso}</td>
                                            <td>{item.total}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RelatoriosLayout;
